import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { runKeywordIntelligence } from "src/common/Scraper";
import { KeywordIntelligenceRunPayload } from "../../types/keyword-assist.type";
import { sleep } from "src/common/config/FunctionHelper";
import useUpdateUserAgents from "../../hooks/useUpdateUserAgents";

interface UseKeywordAssistMutationProps {
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
  onMutateStart?: () => void;
  defaultKeywords?: string[];
}

export function useKeywordAssistMutation({
  onSuccess,
  onError,
  onMutateStart,
  defaultKeywords = []
}: UseKeywordAssistMutationProps = {}) {
  const queryClient = useQueryClient();
  const { mutate: updateAgents } = useUpdateUserAgents('keyword-assist');

  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [keywords, setKeywords] = useState<string[]>(defaultKeywords);

  const runKeywordIntelligenceMutation = useMutation({
    mutationFn: (payload: KeywordIntelligenceRunPayload) =>
      runKeywordIntelligence(payload),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["keywords-assistance", variables?.user_id, variables?.site_id],
      });
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: (error, variables) => {
      if (onError) {
        onError(error);
      }
      handleAgentStatusUpdate(false, variables?.site_id, [])
    },
  });

  function handleAgentStatusUpdate(status: boolean, overrideSiteId?: string, competitorKeywords?: string[]) {
    updateAgents({
      site_id: overrideSiteId,
      agents: [
        {
          name: 'keyword_assist',
          enabled: status,
          config: {
            competitors: competitorKeywords || []
          }
        }
      ]
    });
  }

  const handleEnableKeywordAssist = async (kws: string[], overrides: { siteId: string; userId: string, shouldRun?: boolean }) => {
    const effectiveSiteId = overrides?.siteId;
    const effectiveUserId = overrides?.userId;
    const shouldRun = overrides?.shouldRun ?? true;

    if (onMutateStart) {
      onMutateStart();
    }
    setTimeout(() => {
      setDialogOpen(false);
    }, 2000);

    handleAgentStatusUpdate(true, overrides?.siteId, kws)

    await sleep(1000);

    if (shouldRun) {
      runKeywordIntelligenceMutation.mutate({
        stream: false,
        site_id: effectiveSiteId,
        user_id: effectiveUserId,
        competitor_keywords: kws,
      });
    }



    // handleAgentStatusUpdate(true, overrides?.siteId, kws)

  };

  return {
    dialogOpen,
    setDialogOpen,
    keywords,
    setKeywords,
    handleEnableKeywordAssist,
    runKeywordIntelligenceMutation,
  };
}
